import {
  Badge,
  BadgeProps,
  Box,
  Drawer,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import MessagesApi from '../../apis/MessagesApi';
import SetupApi from '../../apis/SetupApi';
import MobileDrawerToggle from '../../components/MobileDrawerToggle/MobileDrawerToggle';
import NavButton from '../../components/NavButton/NavButton';
import { APP_BAR_HEIGHT, ENV_BAR_HEIGHT } from '../../lib/constants';
import useApi from '../../lib/useApi';
import useFooterControl from '../../lib/useFooterControl';

/**
 * The navigation message badge
 */
const NavMessageBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -13,
    top: 14,
    padding: '0 4px',
  },
}));

/**
 * The admin dashboard
 */
const AdminDashboardView = () => {
  const [showAdminNav, setShowAdminNav] = useState(false);
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const drawerWidth = isMd ? '250px' : '100%';
  const navStyle = {
    justifyContent: isMd ? 'left' : 'center',
    fontSize: isMd ? '1em' : '2em',
    margin: '0 .5em .5em .5em',
  };
  const setupApi = useApi(SetupApi);
  const messagesApi = useApi(MessagesApi);
  const [envBarShowing, setEnvBarShowing] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  useFooterControl(false);

  /**
   * Set the environment bar
   */
  useEffect(() => {
    setupApi.GetEnvironmentBar().then((e) => setEnvBarShowing(e.show));
  }, [setupApi]);

  /**
   * Setup the polling for unread messages every 10 seconds
   */
  useEffect(() => {
    const pollUnreadMessages = async () => {
      try {
        const count = await messagesApi.GetUnreadMessages();
        setUnreadMessages(count);
      } catch (error) {
        console.error('Error polling unread messages:', error);
      }
    };

    pollUnreadMessages();
    const intervalId = setInterval(pollUnreadMessages, 10000);
    return () => clearInterval(intervalId);
  }, [messagesApi]);

  return (
    <Box id="my-box" display="flex" height="100%" width="100%">
      <Drawer
        variant={isMd ? 'permanent' : 'temporary'}
        sx={{
          flexShrink: 0,
          overflow: 'auto',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isMd ? drawerWidth : '100%',
            paddingTop: isMd ? 0 : '1em',
          },
        }}
        anchor={isMd ? 'left' : 'bottom'}
        open={showAdminNav}
        onClick={() => setShowAdminNav(false)}
      >
        <Box
          display="flex"
          height="100%"
          flexDirection="column"
          justifyContent="space-between"
          paddingTop={
            isMd
              ? `${APP_BAR_HEIGHT + (envBarShowing ? ENV_BAR_HEIGHT : 0)}px`
              : 0
          }
        >
          <Box display="flex" flexDirection="column">
            {isMd && (
              <Typography
                variant="h6"
                textAlign="center"
                paddingTop={1}
                paddingBottom={0.5}
              >
                Admin Dashboard
              </Typography>
            )}
            <NavButton to="" end component={NavLink} sx={navStyle}>
              Home
            </NavButton>
            <NavButton to="client-onboarding" component={NavLink} sx={navStyle}>
              Client Onboarding
            </NavButton>
            <NavButton to="articles" component={NavLink} sx={navStyle}>
              Articles
            </NavButton>
            <NavButton to="messages" component={NavLink} sx={navStyle}>
              <NavMessageBadge
                badgeContent={unreadMessages > 0 ? unreadMessages : undefined}
                color="error"
              >
                Messages
              </NavMessageBadge>
            </NavButton>
            <NavButton to="organizations" component={NavLink} sx={navStyle}>
              Organizations
            </NavButton>
            <NavButton to="utilities" component={NavLink} sx={navStyle}>
              Utilities
            </NavButton>
          </Box>
          <MobileDrawerToggle
            drawerState="showing"
            setShowNav={() => setShowAdminNav(false)}
          />
        </Box>
      </Drawer>
      <Box
        display="flex"
        width="100%"
        flexDirection="column"
        justifyContent="space-between"
        marginLeft={isMd ? drawerWidth : 0}
      >
        <Box height="100%" overflow="auto">
          <Outlet context={{ unreadMessages, setUnreadMessages }} />
        </Box>
        {!isMd && (
          <MobileDrawerToggle
            drawerState="hidden"
            setShowNav={() => setShowAdminNav(true)}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdminDashboardView;
